import { yupResolver } from '@hookform/resolvers/yup';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ChevronDown from '@mui/icons-material/KeyboardArrowDown';
import ChevronUp from '@mui/icons-material/KeyboardArrowUp';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { convertLength } from '@turf/helpers';
import dayjs from 'dayjs';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { get, isNull } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import DateRangePicker, {
  DateRangePickerDateSelectFunction,
  NullableDate,
} from '~components/Filters/DateRangePicker';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import Label from '~components/FormFields/v2/Label';
import TextInput from '~components/FormFields/v2/TextInput';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import Tabs from '~components/Tabs/Tabs';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { SupportedAddressProvider } from '~hooks/useAddress/models/AddressProvider';
import { useGeocode } from '~hooks/useGeocode/useGeocode';
import { useStores } from '~store';

export enum FilterByType {
  TRUCK = 'truck',
  DRIVER = 'driver',
}

export enum GeofenceType {
  POLYGON = 'polygon',
  CIRCLE = 'circle',
}

export interface GeofenceConfigurationFormState {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  filterBy: FilterByType;
  location: AddressItem | null;
  latLon: string;
  geofenceType: GeofenceType;
  geofenceName: string;
  drawingCoordinates: { lat: number; lng: number }[];
  circleRadius: number | null;
}

interface GeofenceConfigurationProps {
  onProcess?: (formState: GeofenceConfigurationFormState) => void;
  onLocationChange?: (coordinates: { lat: number; lng: number } | null) => void;
  onDrawingCoordinatesChange?: (
    coordinates: { lat: number; lng: number }[],
    isPolygonMode: boolean,
  ) => void;
  onCircleRadiusChange?: (radius: number | null) => void;
  geofenceRecenterPosition?: { lat: number; lng: number } | null;
  geofenceCircleRadius?: number | null;
  drawingCoordinates?: { lat: number; lng: number }[];
  isProcessing?: boolean;
  isMapPickMode?: boolean;
  onPickLocationModeToggle?: () => void;
  pickedMapCoordinates?: { lat: number; lng: number } | null;
  onPickedMapCoordinatesProcessed?: () => void;
}

// Creates a triangle polygon ~100m around the center point
function createDefaultTrianglePolygon(lat: number, lng: number) {
  return [
    { lat: lat + 0.001, lng: lng },
    { lat: lat - 0.001, lng: lng + 0.001 },
    { lat: lat - 0.001, lng: lng - 0.001 },
  ];
}

function generateDefaultGeofenceName(): string {
  const dateStr = dayjs.tz().format('YYYYMMDD');
  const randomId = Math.floor(Math.random() * 100000);
  return `${dateStr}-polygon-${randomId}`;
}

// Fallback circle radius in meters (300 feet ≈ 91.44 meters) if no company config
const FALLBACK_CIRCLE_RADIUS_METERS = 91.44;

const geofenceConfigurationValidationSchema = yup.object().shape({
  startDate: yup
    .mixed()
    .nullable()
    .required(t('form_validation_errors.required', { field: t('form_fields.date') })),
  endDate: yup.mixed().nullable(), // Not used in single date mode, but kept for form state compatibility
  location: yup
    .object()
    .nullable()
    .test(
      'location-required',
      t('form_validation_errors.required', {
        field: t('live_map.geofence_reports.configuration.location'),
      }) as string,
      function (value) {
        if (!value) {
          return false;
        }
        const addressItem = value as AddressItem;
        // Check that the address has a non-empty placeId (trimmed)
        return Boolean(addressItem.placeId?.trim());
      },
    ),
  geofenceName: yup
    .string()
    .trim()
    .required(
      t('form_validation_errors.required', {
        field: t('live_map.geofence_reports.configuration.geofence_name'),
      }) as string,
    ),
  geofenceType: yup.string(),
  circleRadius: yup
    .number()
    .nullable()
    .when('geofenceType', {
      is: GeofenceType.CIRCLE,
      then: (schema) =>
        schema
          .required(
            t('form_validation_errors.required', {
              field: t('form_fields.radius'),
            }) as string,
          )
          .min(
            1,
            t('form_validation_errors.min', {
              field: t('form_fields.radius'),
              min: 1,
            }) as string,
          )
          .typeError(
            `${t('form_validation_errors.required', { field: t('form_fields.radius') })}`,
          ),
    }),
});

export default function GeofenceConfiguration({
  onProcess,
  onLocationChange,
  onDrawingCoordinatesChange,
  onCircleRadiusChange,
  geofenceRecenterPosition,
  geofenceCircleRadius,
  drawingCoordinates,
  isProcessing,
  isMapPickMode,
  onPickLocationModeToggle,
  pickedMapCoordinates,
  onPickedMapCoordinatesProcessed,
}: GeofenceConfigurationProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isGeofenceNameEditable, setIsGeofenceNameEditable] = useState(false);
  const [geofenceNameBeforeEdit, setGeofenceNameBeforeEdit] = useState('');
  const [dateRange, setDateRange] = useState<{ start: NullableDate; end: NullableDate }>({
    start: dayjs.tz().startOf('day'),
    end: dayjs.tz().endOf('day'), // For single date mode, end is end of the selected day
  });
  const { userStore } = useStores();
  const isFeet = userStore?.userCompany?.isFeet ?? false;

  // Default circle radius in meters (from company config); form stores in display units (feet or meters)
  const defaultCircleRadiusMeters =
    userStore.userCompany?.siteRadius ?? FALLBACK_CIRCLE_RADIUS_METERS;
  const defaultCircleRadiusFeet = parseFloat(
    convertLength(defaultCircleRadiusMeters, 'meters', 'feet').toFixed(2),
  );
  const defaultCircleRadiusDisplay = isFeet
    ? defaultCircleRadiusFeet
    : defaultCircleRadiusMeters;

  const { getPlaces, getAddressItemByGoogleMapsPlaceId, encodeLngLat } = useGeocode({
    addressProvider: SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH,
  });

  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<GeofenceConfigurationFormState>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      startDate: dayjs.tz().startOf('day'),
      endDate: dayjs.tz().endOf('day'), // For single date mode, endDate is end of the selected day
      filterBy: FilterByType.DRIVER,
      location: null,
      latLon: '',
      geofenceType: GeofenceType.POLYGON,
      geofenceName: generateDefaultGeofenceName(),
      drawingCoordinates: [],
      circleRadius: null,
    },
    resolver: yupResolver(geofenceConfigurationValidationSchema),
  });

  const formState = watch();
  const geoCodedAddress = watch('location');
  const watchedCircleRadius = watch('circleRadius');

  const resetLatLon = useCallback(() => {
    if (geoCodedAddress) {
      setValue('latLon', `${geoCodedAddress.lat},${geoCodedAddress.lng}`);
    } else {
      setValue('latLon', '');
    }
  }, [geoCodedAddress, setValue]);

  const handleDateRangeChange = useCallback<DateRangePickerDateSelectFunction>(
    (startDate) => {
      const start = startDate;
      // For single date mode, endDate should be the end of the selected day
      const end = startDate ? startDate.endOf('day') : null;

      setDateRange({ start, end });
      setValue('startDate', start);
      setValue('endDate', end);
    },
    [setValue],
  );

  useEffect(() => {
    resetLatLon();
    if (geoCodedAddress) {
      onLocationChange?.({ lat: geoCodedAddress.lat, lng: geoCodedAddress.lng });

      // Create default polygon if geofence type is POLYGON
      if (formState.geofenceType === GeofenceType.POLYGON) {
        const newCoordinates = createDefaultTrianglePolygon(
          geoCodedAddress.lat,
          geoCodedAddress.lng,
        );
        setValue('drawingCoordinates', newCoordinates);
        onDrawingCoordinatesChange?.(newCoordinates, true);
      } else if (formState.geofenceType === GeofenceType.CIRCLE) {
        setValue('circleRadius', defaultCircleRadiusDisplay);
        onCircleRadiusChange?.(defaultCircleRadiusMeters);
      }
    } else {
      onLocationChange?.(null);
      setValue('drawingCoordinates', []);
      setValue('circleRadius', null);
      onDrawingCoordinatesChange?.([], false);
      onCircleRadiusChange?.(null);
    }
  }, [geoCodedAddress?.placeId]);

  // Handle geofence type changes
  useEffect(() => {
    const isPolygonMode = formState.geofenceType === GeofenceType.POLYGON;
    const isCircleMode = formState.geofenceType === GeofenceType.CIRCLE;

    if (isPolygonMode && formState.drawingCoordinates.length === 0) {
      // Use current pin position (e.g. after dragging circle) so polygon matches the pin
      let pinLat: number;
      let pinLng: number;
      if (
        typeof geofenceRecenterPosition?.lat === 'number' &&
        typeof geofenceRecenterPosition?.lng === 'number'
      ) {
        pinLat = geofenceRecenterPosition.lat;
        pinLng = geofenceRecenterPosition.lng;
      } else {
        const latLonParts = formState.latLon.split(',').map((s) => s.trim());
        if (
          latLonParts.length === 2 &&
          latLonParts.every((p) => p !== '' && !Number.isNaN(parseFloat(p)))
        ) {
          pinLat = parseFloat(latLonParts[0]);
          pinLng = parseFloat(latLonParts[1]);
        } else if (geoCodedAddress) {
          pinLat = geoCodedAddress.lat;
          pinLng = geoCodedAddress.lng;
        } else {
          pinLat = NaN;
          pinLng = NaN;
        }
      }
      if (!Number.isNaN(pinLat) && !Number.isNaN(pinLng)) {
        const newCoordinates = createDefaultTrianglePolygon(pinLat, pinLng);
        setValue('drawingCoordinates', newCoordinates);
        onDrawingCoordinatesChange?.(newCoordinates, true);
      }
      // Clear circle radius when switching to polygon mode
      setValue('circleRadius', null);
      onCircleRadiusChange?.(null);
    } else if (isCircleMode) {
      // Clear drawing coordinates when switching to circle mode
      setValue('drawingCoordinates', []);
      onDrawingCoordinatesChange?.([], false);
      // Set default circle radius when switching to circle mode with existing coordinates
      if (geoCodedAddress && isNull(formState.circleRadius)) {
        setValue('circleRadius', defaultCircleRadiusDisplay);
        onCircleRadiusChange?.(defaultCircleRadiusMeters);
      }
    } else {
      // Just notify about current state
      // Only enable drawing mode if we have an address selected
      const shouldEnableDrawingMode = isPolygonMode && !!geoCodedAddress;
      onDrawingCoordinatesChange?.(formState.drawingCoordinates, shouldEnableDrawingMode);
    }
  }, [formState.geofenceType]);

  // Sync parent drawing coordinates to form state when polygon is edited on the map
  useEffect(() => {
    if (drawingCoordinates && drawingCoordinates.length > 0) {
      setValue('drawingCoordinates', drawingCoordinates);
    }
  }, [drawingCoordinates, setValue]);

  // Sync form when circle is moved/resized on the map (center and radius in meters from parent).
  // Do not overwrite radius when parent's value is just the rounded form value (301 ft → 91.74 m → parent rounds to 92 m → would overwrite to 302 ft).
  useEffect(() => {
    if (
      formState.geofenceType !== GeofenceType.CIRCLE ||
      typeof geofenceRecenterPosition?.lat !== 'number' ||
      typeof geofenceRecenterPosition?.lng !== 'number' ||
      typeof geofenceCircleRadius !== 'number' ||
      geofenceCircleRadius <= 0
    ) {
      return;
    }
    setValue('latLon', `${geofenceRecenterPosition.lat},${geofenceRecenterPosition.lng}`);
    onLocationChange?.(geofenceRecenterPosition);
    const formRadiusMeters =
      watchedCircleRadius !== null && watchedCircleRadius !== undefined
        ? isFeet
          ? convertLength(watchedCircleRadius, 'feet', 'meters')
          : watchedCircleRadius
        : null;
    const parentIsJustRoundedFormValue =
      formRadiusMeters !== null &&
      Math.round(formRadiusMeters) === Math.round(geofenceCircleRadius);
    if (!parentIsJustRoundedFormValue) {
      const radiusDisplay = isFeet
        ? Math.round(convertLength(geofenceCircleRadius, 'meters', 'feet'))
        : geofenceCircleRadius;
      setValue('circleRadius', radiusDisplay);
    }
  }, [
    geofenceRecenterPosition,
    geofenceCircleRadius,
    formState.geofenceType,
    setValue,
    onLocationChange,
    isFeet,
  ]);

  // Handle coordinates picked from the map
  useEffect(() => {
    if (pickedMapCoordinates) {
      const coordString = `${pickedMapCoordinates.lat},${pickedMapCoordinates.lng}`;

      // Update the lat/lon text field with the picked coordinates
      setValue('latLon', coordString);
      // Notify parent about the location change
      onLocationChange?.(pickedMapCoordinates);

      // Reverse geocode to get the address and set it in the Location field
      encodeLngLat(coordString).then((address) => {
        if (address && address.placeId) {
          setValue('location', address);
        }
      });

      // Create default geofence based on the current geofence type
      if (formState.geofenceType === GeofenceType.POLYGON) {
        const newCoordinates = createDefaultTrianglePolygon(
          pickedMapCoordinates.lat,
          pickedMapCoordinates.lng,
        );
        setValue('drawingCoordinates', newCoordinates);
        onDrawingCoordinatesChange?.(newCoordinates, true);
      } else if (formState.geofenceType === GeofenceType.CIRCLE) {
        setValue('circleRadius', defaultCircleRadiusDisplay);
        onCircleRadiusChange?.(defaultCircleRadiusMeters);
      }

      // Clear the picked coordinates after processing to prevent stale state on remount
      onPickedMapCoordinatesProcessed?.();
    }
  }, [pickedMapCoordinates]);

  const handleProcess = async () => {
    const isValid = await trigger([
      'startDate',
      'endDate',
      'location',
      'geofenceName',
      'circleRadius',
    ]);
    if (!isValid) {
      return;
    }
    onProcess?.(formState);
  };

  return (
    <Box sx={{ borderBottom: (theme) => `1px solid ${theme.brandV2.colors.treadGray7}` }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography
          sx={(theme) => ({
            fontSize: '14px',
            fontWeight: 600,
            color: theme.brandV2.colors.treadBlack,
          })}
        >
          {t('live_map.geofence_reports.configuration.title')}
        </Typography>
        <IconButton size="small">
          {isExpanded ? <ChevronUp /> : <ChevronDown />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Label>{t('form_fields.date')}</Label>
            <DateRangePicker
              endDate={dateRange.end}
              mode="icon"
              onDateSelect={handleDateRangeChange}
              showShortcuts
              startDate={dateRange.start}
              useTodayFormat
              variant="single_date"
            />
          </Box>

          <Box>
            <Label>{t('live_map.geofence_reports.configuration.filter_by')}</Label>
            <Tabs
              selected={formState.filterBy}
              onChange={(newValue: FilterByType) => setValue('filterBy', newValue)}
              tabs={[
                {
                  label: t('live_map.geofence_reports.configuration.by_driver'),
                  value: FilterByType.DRIVER,
                },
                {
                  label: t('live_map.geofence_reports.configuration.by_truck'),
                  value: FilterByType.TRUCK,
                },
              ]}
            />
          </Box>

          <Box
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              width: '100%',
            }}
          >
            <Label>{t('live_map.geofence_reports.configuration.location')}</Label>

            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray3,
                fontSize: '11px',
              })}
            >
              {t('live_map.geofence_reports.configuration.select_location_hint')}
            </Typography>

            <Box sx={{ alignItems: 'flex-end', display: 'flex', gap: 1, width: '100%' }}>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <AutocompleteAsyncFormField
                  asyncCallback={getPlaces}
                  clearable={true}
                  control={control}
                  debounceTime={500}
                  errors={errors}
                  getInputValue={(item) => {
                    if (item.fullAddress) {
                      return item.fullAddress;
                    }
                    const isFullAddress = item._thoroughfare?.includes(',');
                    return isFullAddress
                      ? `${item.shortName} ${item._locality}, ${item._administrative_area} ${item._postal_code}`
                      : `${item._thoroughfare} ${item._locality}, ${item._administrative_area} ${item._postal_code}`;
                  }}
                  getLabel={(item) => get(item, 'nameWithAddress', '')}
                  getValue={(item) => get(item, 'placeId', '')}
                  onSelect={async (selectedAddress: AddressItem) => {
                    return await getAddressItemByGoogleMapsPlaceId(
                      selectedAddress.placeId,
                      selectedAddress.fullAddress ?? undefined,
                    );
                  }}
                  placeholder={`${t('live_map.geofence_reports.configuration.search_address')}`}
                  label=""
                  name="location"
                />

                <Tooltip
                  title={t('live_map.geofence_reports.configuration.pick_location_hint')}
                >
                  <IconButton
                    onClick={onPickLocationModeToggle}
                    size="small"
                    sx={(theme) => ({
                      mt: '5px',
                      bgcolor: isMapPickMode
                        ? theme.brandV2.colors.treadGreen
                        : 'transparent',
                      color: isMapPickMode ? 'white' : 'inherit',
                      '&:hover': {
                        bgcolor: isMapPickMode
                          ? theme.brandV2.colors.treadGreen
                          : 'action.hover',
                      },
                    })}
                  >
                    <PlaceOutlined />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <TextInput
              control={control}
              name="latLon"
              slotProps={{ htmlInput: { readOnly: true } }}
              sx={(theme) => ({
                [`& .${inputBaseClasses.root}`]: {
                  bgcolor: theme.brandV2.colors.treadGray10,
                },
              })}
            />
          </Box>

          <Box>
            <Label>{t('live_map.geofence_reports.configuration.geofence_type')}</Label>
            <Tabs
              selected={formState.geofenceType}
              onChange={(newValue: GeofenceType) => {
                setValue('geofenceType', newValue);
                setValue(
                  'geofenceName',
                  formState.geofenceName.includes('polygon')
                    ? formState.geofenceName.replace('polygon', newValue)
                    : formState.geofenceName.replace('circle', newValue),
                );
              }}
              tabs={[
                {
                  label: t('live_map.geofence_reports.configuration.polygon'),
                  value: GeofenceType.POLYGON,
                },
                {
                  label: t('live_map.geofence_reports.configuration.circle'),
                  value: GeofenceType.CIRCLE,
                },
              ]}
            />
          </Box>

          {formState.geofenceType === GeofenceType.CIRCLE && (
            <Controller
              control={control}
              name="circleRadius"
              render={({ field: { value, onChange, onBlur, ref } }) => {
                const error = get(errors, 'circleRadius');
                return (
                  <FormControl
                    fullWidth
                    error={Boolean(error)}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
                  >
                    <Label>{`${t('form_fields.radius')} (${isFeet ? t('units_of_distance.feet') : t('units_of_distance.meters')})`}</Label>
                    <TextField
                      inputRef={ref}
                      slotProps={{ htmlInput: { min: 1, step: isFeet ? 1 : 0.01 } }}
                      type="number"
                      value={value ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || v === null || v === undefined) {
                          onChange(null);
                          return;
                        }
                        const num = Number(v);
                        if (Number.isNaN(num)) return;
                        onChange(isFeet ? Math.round(num) : num);
                      }}
                      onBlur={() => {
                        onBlur();
                        const radiusInMeters =
                          value !== null && value !== undefined
                            ? isFeet
                              ? convertLength(Number(value), 'feet', 'meters')
                              : Number(value)
                            : null;
                        onCircleRadiusChange?.(radiusInMeters);
                      }}
                      onWheel={(event) => (event.target as HTMLElement)?.blur()}
                      onKeyDown={(event) => {
                        if (event.key === 'e' || event.key === 'E') {
                          event.preventDefault();
                        }
                      }}
                    />
                    {error?.message && (
                      <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
                        {error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                );
              }}
            />
          )}

          <TextInput
            control={control}
            name="geofenceName"
            label={t('live_map.geofence_reports.configuration.geofence_name')}
            sx={(theme) => ({
              [`& .${inputBaseClasses.root}`]: {
                bgcolor: isGeofenceNameEditable
                  ? 'transparent'
                  : theme.brandV2.colors.treadGray10,
              },
            })}
            slotProps={{
              htmlInput: { readOnly: !isGeofenceNameEditable },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {isGeofenceNameEditable ? (
                      <>
                        <IconButton
                          color="success"
                          onClick={() => setIsGeofenceNameEditable(false)}
                          size="small"
                        >
                          <CheckOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setValue('geofenceName', geofenceNameBeforeEdit);
                            setIsGeofenceNameEditable(false);
                          }}
                          size="small"
                        >
                          <CloseOutlined fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setGeofenceNameBeforeEdit(formState.geofenceName);
                          setIsGeofenceNameEditable(true);
                        }}
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              },
            }}
          />

          <SmallLoadingButton
            fullWidth
            onClick={handleProcess}
            loading={isProcessing}
            disabled={isProcessing}
            variant="contained"
          >
            {t('live_map.geofence_reports.configuration.process')}
          </SmallLoadingButton>
        </Box>
      </Collapse>
    </Box>
  );
}
